import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { BuilderInput, FieldSpec, StructuredBuilderDef } from '@/lib/editor/types';
import { FormSection } from './FormSection';
import { StructuredSectionFields } from './StructuredSectionFields';
import { ToggleTab } from './ToggleTab';
import type { ActivateFn, Translate } from './types';

export function StructuredBuilderForm({
  formRef,
  def,
  input,
  outcome,
  rejectReason,
  expert,
  setExpert,
  patch,
  setOutcome,
  setRejectReason,
  highlighted,
  onActivate,
  onReset,
  fieldByKey,
  t,
}: {
  formRef: RefObject<HTMLElement | null>;
  def: StructuredBuilderDef;
  input: BuilderInput;
  outcome: string;
  rejectReason: string;
  expert: boolean;
  setExpert: (v: boolean) => void;
  patch: (key: string, value: string | boolean) => void;
  setOutcome: (v: string) => void;
  setRejectReason: (v: string) => void;
  highlighted: string | null;
  onActivate: ActivateFn;
  onReset: () => void;
  fieldByKey: (key: string) => FieldSpec | undefined;
  t: Translate;
}) {
  const sections = expert ? def.expertSections : def.simpleSections;
  const sectionProps = {
    input,
    outcome,
    rejectReason,
    patch,
    setOutcome,
    setRejectReason,
    highlighted,
    onActivate,
    fieldByKey,
    outcomeSpec: def.outcome,
    builderShort: def.short,
    t,
  };

  const outcomeKeys = def.outcomeFieldKeys ?? ['outcome'];
  const showOutcomeInSimple = !expert && outcomeKeys.length > 0;

  return (
    <aside ref={formRef} className="panel space-y-3 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="eyebrow">{t('try.fields')}</h2>
        <div className="flex items-center gap-px">
          <ToggleTab size="sm" active={!expert} onClick={() => setExpert(false)}>
            {t('try.modeSimple')}
          </ToggleTab>
          <ToggleTab size="sm" active={expert} onClick={() => setExpert(true)}>
            <SlidersHorizontal size={11} aria-hidden />
            {t('try.modeExpert')}
          </ToggleTab>
          <button
            type="button"
            onClick={onReset}
            className="ml-1 inline-flex items-center gap-1 border border-rule px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted hover:border-ink hover:text-ink"
          >
            <RotateCcw size={11} aria-hidden />
            {t('try.reset')}
          </button>
        </div>
      </div>
      {expert && <p className="text-[11px] leading-snug text-muted">{t('try.expertHint')}</p>}

      {sections.map((section) => (
        <StructuredSectionFields key={section.id} section={section} {...sectionProps} />
      ))}

      {showOutcomeInSimple && (
        <FormSection icon={SlidersHorizontal} title={t(`try.${def.short.split('.')[0]}.sectionStatus`)}>
          <StructuredSectionFields
            section={{ id: 'outcome', titleKey: 'try.sectionStatus', keys: [...outcomeKeys] }}
            {...sectionProps}
          />
        </FormSection>
      )}

      <p className="text-[12px] leading-relaxed text-muted">
        {t('try.related')}{' '}
        <Link to={`/messages/${def.short}`} className="text-signal hover:underline">
          {def.short}
        </Link>
        {' · '}
        <Link to={`/messages/${def.pairShort}`} className="text-signal hover:underline">
          {def.pairShort}
        </Link>
      </p>
    </aside>
  );
}
